import AWSS3UploadAsh from 'aws-s3-upload-ash';
import ControlledDropFiles from 'app/atoms/ControlledDropFiles/ControlledDropFiles';
import { Revenue } from 'app/atoms/PropertyInformation/PropertyInformation';
import SliderControlled from 'app/atoms/SliderControlled/SliderControlled';
import { toast } from 'app/toaster/toast';
import Close from 'assets/Close';
import { useEffect, useState } from 'react';
import { Col, Form, InputGroup } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { Link } from 'react-router-dom';
import usePost from 'services/usePost';
import { isEmpty, toNDecimals } from 'utils/utils';
import dragYourImagesHere from '../../../../assets/be-an-urbain-owner/drag-your-images-here.svg';
import './PropertyAdditionalInformation.scss';
import PDFReader from '../../../atoms/PDFReader/PDFReader';

interface PropertyAdditionalInformation {
  UUID?: string;
  UploadBucket?: string;
  UploadPrefix?: string;
  UploadRegion?: string;
  UploadKey?: string;
  UploadSecret?: string;
  UploadS3URL?: string;
  PropertyExtendedAddress?: string;
}

function PropertyAdditionalInformation({
  UUID,
  UploadBucket,
  UploadPrefix,
  UploadRegion,
  UploadKey,
  UploadSecret,
  UploadS3URL,
  PropertyExtendedAddress
}: PropertyAdditionalInformation) {
  const {
    register,
    control,
    trigger,
    watch,
    handleSubmit,
    formState: { errors }
  } = useFormContext();
  const formData = watch();

  const [calculatedValue, setCalculatedValue] = useState<Revenue | null>();
  const [calculatorHasError, setCalculatorHasError] = useState<boolean>(false);
  const [submitResult, setSubmitResult] = useState<any>();
  const [showThankYouMessage, setShowThankYouMessage] = useState<boolean>(false);
  const [calculateGrossRevenueResult, calculateGrossRevenue] = usePost<any>({
    url: '/calculate',
    defaultValue: {},
    successMessage: ''
  });

  useEffect(() => {
    console.log(formData);
  }, [formData]);

  async function calculateGrossRevenueHandler() {
    if (await trigger(['PropertyAverageRentalWeeks', 'PropertyPostalCode', 'PropertyCountryName', 'PropertyBedrooms'])) {
      setCalculatorHasError(false);
      setCalculatedValue(null);
      calculateGrossRevenue({
        PropertyAverageRentalWeeks: formData.PropertyAverageRentalWeeks,
        PropertyBathrooms: formData.PropertyBathrooms,
        PropertyBedrooms: formData.PropertyBedrooms,
        PropertyCountryName: formData.PropertyCountryName,
        PropertyPostalCode: formData.PropertyPostalCode?.split(' ')[0],
        PropertyQuality: formData.PropertyQuality
      });
    } else {
      toast.error('Please complete all the fields, and try again.');
    }
  }

  useEffect(() => {
    if (!isEmpty(calculateGrossRevenueResult) && !isEmpty(calculateGrossRevenueResult.data) && !('error' in calculateGrossRevenueResult.data)) {
      setCalculatedValue({
        grossHigh: toNDecimals(calculateGrossRevenueResult.data.grossHigh),
        grossLow: toNDecimals(calculateGrossRevenueResult.data.grossLow),
        grossRevenue: toNDecimals(calculateGrossRevenueResult.data.grossRevenue)
      });
    } else if (calculateGrossRevenueResult && calculateGrossRevenueResult.data && 'error' in calculateGrossRevenueResult.data) {
      setCalculatorHasError(true);
    }
  }, [calculateGrossRevenueResult]);

  const [submitFormResult, submitForm] = usePost<any>({
    url: '/beanowner',
    defaultValue: {},
    successMessage: ''
  });

  useEffect(() => {
    !isEmpty(submitFormResult) && setSubmitResult(submitFormResult);
    !isEmpty(submitFormResult) && uploadToAWS(formData?.PropertyPictures);
  }, [submitFormResult]);

  async function uploadToAWS(pictures: any) {
    const config = {
      bucketName: UploadBucket || '',
      dirName: UploadPrefix || '',
      region: UploadRegion || '',
      accessKeyId: UploadKey || '',
      secretAccessKey: UploadSecret || '',
      s3Url: UploadS3URL
    };

    const S3Client = new AWSS3UploadAsh(config);

    try {
      await Promise.all(
        pictures.map(async (pic: any, index: number) => {
          S3Client.uploadFile(pic.file, pic.file.type, undefined, `${index.toString()}.${pic.file.name.split('.').pop()}`, 'public-read');
        })
      )
        .then(res => {
          //open message
          setShowThankYouMessage(true);
        })
        .catch(err => {
          // error message
          console.log('CATCH: ', err);
        });
    } catch (error) {
      console.log(error);
    }
  }

  return (
    <div className="property-additional-information col-12 col-sm-8">
      <div className="calculator-title">Potential revenue calculator</div>
      <div className="property-address-header">
        Property address: <span className="property-address">{PropertyExtendedAddress} </span>
      </div>
      <div className="slider-container">
        <div className="slider-and-title">
          <div className="slider-title">Property Quality</div>
          <div className="slider-and-label">
            <SliderControlled name="PropertyQuality" rules={{ required: true }} step={1} max={5} min={-5} defaultValue={0} showTooltip={false} />
            <div className="slider-label">
              <div>Simple Comfort</div>
              <div>Great Quality</div>
              <div>True Luxury</div>
            </div>
          </div>
        </div>
      </div>
      <div className="slider-container">
        <div className="slider-and-title">
          <div className="slider-title">Desired Annual Rental Weeks</div>
          <div className="slider-and-label">
            <SliderControlled name="PropertyAverageRentalWeeks" rules={{ required: true }} step={1} max={52} min={5} defaultValue={28} />
            <div className="slider-label">
              <div>5</div>
              <div>28</div>
              <div>52</div>
            </div>
          </div>
        </div>
      </div>
      <div className="bottom-note">
        Press "calculate" to get an estimate of the rental income your property could generate by partnering with Urbain, based on your property's
        location and basic details:
      </div>
      <div className="calculate-section">
        <button
          className={`calculate-button ${
            (!!calculatedValue && !isEmpty(calculatedValue)) || calculatorHasError ? 'calculate-button-full-width' : ''
          }`}
          onClick={calculateGrossRevenueHandler}
        >
          Calculate
        </button>
        {calculatedValue && (
          <div className="calculate-result-card">
            <div className="result">
              <div className="value">${calculatedValue.grossLow?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</div>
              <div className="text">to</div>
              <div className="value">${calculatedValue.grossHigh.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</div>
              <div className="text">per year</div>
            </div>
            <div className="commentary">
              Please note that this is only a rough estimated range of gross revenues and rates are before Urbain commission and cleaning costs are
              deducted. Amounts shown are based on an average property within the profile you’ve  selected. Earnings typically vary week to week, with
              revenues likely being higher during peak times of the year than in off-peak weeks. We’ll be happy to provide a more accurate rental
              forecast when you partner with Urbain.
            </div>
          </div>
        )}
        {calculatorHasError && (
          <div className="calculate-result-card">
            <div className="commentary-error">
              Thank you for your interest in partnering with Urbain! While we don't currently feature your location, we are continually adding new
              locations, so please submit the contact form below to be notified as soon as we’re in your area. In the meantime,{' '}
              <Link to="/about-us">click here</Link> to learn more about Urbain.
            </div>
          </div>
        )}
      </div>
      <div className="rental-history">
        <div className="rental-history-title">Have you ever rented out your property for temporary stays before?</div>
        <div className="rental-history-radio-container">
          <Form.Check
            value="yes"
            {...register('PropertyPreviouslyRented')}
            className="rental-history-radio"
            type={'radio'}
            id="yes"
            label="Yes, currently"
          />
          <Form.Check
            value="not-currently"
            {...register('PropertyPreviouslyRented')}
            className="rental-history-radio"
            type={'radio'}
            id="no-right-now"
            label="Yes, but not currently"
          />
          <Form.Check
            value="never"
            {...register('PropertyPreviouslyRented')}
            className="rental-history-radio"
            type={'radio'}
            id="never"
            label="Never"
          />
        </div>
        <div className="rental-history-input-container">
          <Form.Row>
            <Form.Group as={Col} controlId="formGridRentalHistoryPlatforms" xs={12}>
              <Form.Label className="rental-history-form-label-info required">
                Do you use any online property listing platforms? (ie: airbnb, booking.com, VRBO, etc.){' '}
              </Form.Label>
              <InputGroup className="mb-3">
                <Form.Control {...register('UsePropertyListingPlatforms')} />
              </InputGroup>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} controlId="formGridRentalHistoryHowLong" xs={12}>
              <Form.Label className="rental-history-form-label-info required">How long have you used this address as a rental property? </Form.Label>
              <InputGroup>
                <Form.Control className="mb-3" {...register('HowLongHasThisAddressRented')} />
              </InputGroup>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} controlId="formGridRentalHistoryAvailability" xs={12}>
              <Form.Label className="rental-history-form-label-info required">How many weeks in a year is your property available? </Form.Label>
              <InputGroup>
                <Form.Control {...register('HowManyWeeksPerYearAvailable')} />
              </InputGroup>
            </Form.Group>
          </Form.Row>
        </div>
        <Form.Group controlId="formGridRentalHistoryTellUs">
          <Form.Label className="rental-history-form-label-info required">
            Tell us a bit about your property. Key amenities, special touches, history, etc.{' '}
          </Form.Label>
          <Form.Control as="textarea" rows={3} {...register('TellUsMore')} />
        </Form.Group>
      </div>
      <div className="url-and-pics">
        <div className="url-and-pics-title">Use this space to upload pictures and/or share URLs of your property.</div>
        <Form.Row>
          <Form.Group as={Col} controlId="formGridRentalPropertyUrl" xs={12}>
            <InputGroup className="mb-3">
              <Form.Control placeholder="URL" {...register('url')} />
            </InputGroup>
          </Form.Group>
        </Form.Row>
        <Form.Row>
          {!!errors.PropertyPictures && (
            <Form.Control.Feedback type="invalid" className="display-block">
              {errors.PropertyPictures.message || 'Error'}
            </Form.Control.Feedback>
          )}
          <div className="pic-drag-and-drop">
            <ControlledDropFiles
              name="PropertyPictures"
              label={
                <div className="d-flex flex-column">
                  <img className="drag-and-drop-icon" src={dragYourImagesHere} alt="drag your images here" />
                  <div className="drag-and-drop-title">Drag your images here or click to upload. </div>
                </div>
              }
            />
          </div>
        </Form.Row>
      </div>
      <div className="submit-button-container">
        <button
          className="submit-button"
          onClick={handleSubmit(form => {
            submitForm(form);
          })}
        >
          Submit Form
        </button>
      </div>
      {!!submitResult && !isEmpty(submitResult) && showThankYouMessage && (
        <div className="submit-result-card">
          <div className="result">Great News!</div>
          <div className="commentary">
            We currently feature your location! We will reach out to you very shortly, using the contact information you’ve provided. In the meantime,{' '}
            <Link to="/about-us">click here</Link> to learn more about Urbain.
          </div>
          <Close onClick={() => setSubmitResult(null)} className="__close-icon close-button" />
        </div>
      )}
      <PDFReader filePath={'./UrbainBDIntro.pdf'} />
    </div>
  );
}
export default PropertyAdditionalInformation;
